import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";

const API = process.env.REACT_APP_API_URL;

function isAllowedPathway(pathway) {
  if (!pathway || typeof pathway !== "object") return false;
  const raw = String(pathway.label || pathway.value || pathway.pathway_name || "");
  return !raw.toLowerCase().includes("_adjusted");
}

export default function FairsharePage() {
  const { accessToken, csrfToken, logout, selectedScenathon } = useAuth();
  const [loadingScenathons, setLoadingScenathons] = useState(true);
  const [scenathons, setScenathons] = useState([]);
  const [scenathonId, setScenathonId] = useState(
    selectedScenathon?.id ? String(selectedScenathon.id) : ""
  );
  const [pathwayId, setPathwayId] = useState("");
  const [pathways, setPathways] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);
  const [runningFairshare, setRunningFairshare] = useState(false);
  const [error, setError] = useState("");

  const apiFetch = async (url, opts = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(opts.headers || {}),
    };

    const res = await fetch(url, { credentials: "include", ...opts, headers });
    if (res.status === 401) {
      logout?.();
      throw new Error("Session expired");
    }
    return res;
  };

  useEffect(() => {
    let alive = true;
    setLoadingScenathons(true);

    apiFetch(`${API}/api/scenathon/scenathons`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        setScenathons(Array.isArray(data?.scenathons) ? data.scenathons : []);
      })
      .catch((e) => {
        console.error(e);
        if (alive) setScenathons([]);
      })
      .finally(() => {
        if (alive) setLoadingScenathons(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!scenathonId) {
      setPathways([]);
      setPathwayId("");
      return;
    }

    setLoadingPathways(true);
    let alive = true;

    apiFetch(`${API}/api/scenathon/available-pathways/${scenathonId}`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        const nextPathways = Array.isArray(data.pathways) ? data.pathways : [];
        const filteredPathways = nextPathways.filter(isAllowedPathway);
        setPathways(filteredPathways);
        setPathwayId((prev) => (
          filteredPathways.some((pathway) => String(pathway.value) === String(prev))
            ? prev
            : ""
        ));
      })
      .catch((e) => {
        console.error(e);
        if (alive) setPathways([]);
      })
      .finally(() => {
        if (alive) setLoadingPathways(false);
      });

    return () => {
      alive = false;
    };
  }, [scenathonId]);

  useEffect(() => {
    if (selectedScenathon?.id && String(selectedScenathon.id) !== scenathonId) {
      setScenathonId(String(selectedScenathon.id));
    }
  }, [selectedScenathon?.id, scenathonId]);

  const scenathonOptions = useMemo(
    () => scenathons.map((s) => ({ value: String(s.id), label: s.name || `Scenathon ${s.id}` })),
    [scenathons],
  );

  const getPathwayName = () => {
    const found = pathways.find((p) => String(p.value) === String(pathwayId));
    return found?.label || pathwayId;
  };

  const handleRunFairshare = async () => {
    if (!scenathonId || !pathwayId) {
      setError("Please select a scenathon and pathway first.");
      return;
    }

    setError("");
    setRunningFairshare(true);

    try {
      const res = await apiFetch(`${API}/api/fair/run`, {
        method: "POST",
        body: JSON.stringify({
          scenathonId: Number(scenathonId),
          pathwayId: Number(pathwayId),
          pathwayName: getPathwayName(),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      Swal.fire({
        icon: "success",
        title: "Fairshare executed",
        text: `Inserted ${data.insertedCount ?? data.rowCount ?? 0} rows into ${data.tableName || "the fairshare table"}.`,
      });
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message || "Failed to run fairshare",
      });
    } finally {
      setRunningFairshare(false);
    }
  };

  return (
    <div className="mt-10 w-full rounded-xl bg-slate-50 shadow-sm">
      <main className="mx-auto w-full px-4 py-8 sm:px-6 lg:px-4">
        <div className="rounded-xl bg-white p-6 ring-1 ring-[#E3E5E6]">
          <h1 className="text-2xl font-semibold text-slate-900">Fairshare</h1>
          <p className="mt-1 text-sm text-slate-600">
            Run fairshare processing for a selected scenathon and pathway.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {!selectedScenathon?.id && (
              <Select
                label="Scenathon"
                value={scenathonId}
                onChange={setScenathonId}
                options={scenathonOptions}
                placeholder="Select scenathon"
                className="w-full"
              />
            )}

            <Select
              label="Pathway"
              value={pathwayId}
              onChange={setPathwayId}
              options={pathways}
              placeholder={
                scenathonId
                  ? loadingPathways ? "Loading pathways..." : "Select pathway"
                  : "Select scenathon first"
              }
              disabled={!scenathonId || loadingPathways}
              className="w-full"
            />
          </div>

          {error && (
            <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleRunFairshare}
              disabled={!scenathonId || !pathwayId || runningFairshare}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {runningFairshare ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Running fairshare...
                </>
              ) : (
                "Run fairshare"
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
